"use client";

import { useRouter } from "next/navigation";
import { CircleAlert, TriangleAlert } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { Progress } from "@/components/shadcn/progress";
import { Separator } from "@/components/shadcn/separator";
import { useStorageStatus } from "@/services/Storage/useStorageStatus";
import { useCanDeleteHistory } from "@/services/Storage/useCanDeleteHistory";
import { formatSizeInBytes } from "@/utils/generic";

// Both levels get the same dialog; only the colour, icon and verbs change.
// Copy stays split by role: telling someone who cannot open Past usage to go
// delete things there is worse than telling them to find an admin.
const useDialogCopy = () => {
  // Deleting history is a platform-admin action, so only they are told to do
  // it — everyone else is pointed at a person, not a page they can't use.
  const canFreeUpSpace = useCanDeleteHistory();
  const { status } = useStorageStatus();

  // The dialog is raised by a refused request, so the disk is full whatever the
  // status endpoint has to say — it only supplies the numbers.
  const isCritical = status ? status.level === "critical" : true;
  const usage = status
    ? `${Math.round(status.used_percentage)}% of ${formatSizeInBytes(
        status.total_bytes,
        0
      )} used, ${formatSizeInBytes(status.free_bytes)} free`
    : "Disk space exhausted";
  // The thresholds are effective figures: the percentage or the absolute
  // floor, whichever the server resolved for this disk size.
  const blockedUnder = status
    ? formatSizeInBytes(status.critical_free_bytes)
    : null;
  const warningUnder = status
    ? formatSizeInBytes(status.warning_free_bytes)
    : null;

  return {
    isCritical,
    usage,
    percentage: status?.used_percentage ?? 100,
    title: isCritical ? "No disk space left" : "Disk space critically low",
    // The alert box carries the state and the urgency; the impact belongs
    // underneath it, or the box reads as "these are the only things affected".
    urgency: isCritical
      ? "Immediate action required. Platform functionality is disrupted."
      : "Immediate action required to prevent platform disruption.",
    thresholds:
      warningUnder && blockedUnder
        ? `Warning under ${warningUnder} free, blocked under ${blockedUnder} free.`
        : null,
    // A heading rather than a sentence: it reads as a countdown at warning level
    // and as a statement of fact once the disk is full.
    consequencesHeading: isCritical
      ? `Free space is under ${blockedUnder ?? "the limit"}`
      : `If free space drops under ${blockedUnder ?? "the limit"}`,
    consequences: isCritical
      ? [
          "Flows no longer run",
          "Deployments not functioning",
          "Agent chats can't be created",
          "Case management not functioning",
          "Evaluations not running",
          "KB ingestions not running",
        ]
      : [
          "Flows will no longer run",
          "Deployments stop functioning",
          "Agent chats can't be created",
          "Case management intake stops",
          "Evaluations stop running",
          "KB ingestion stops",
        ],
    catchAll: "Other operations may also be affected",
    solution: canFreeUpSpace
      ? "Delete historic runs and chats in Past usage to free up space."
      : "Please reach out to a platform administrator.",
    canFreeUpSpace,
  };
};

export const StorageLimitDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const router = useRouter();
  const copy = useDialogCopy();

  if (!isOpen) return null;

  const Icon = copy.isCritical ? CircleAlert : TriangleAlert;
  const tone = copy.isCritical ? "text-destructive" : "text-amber-600";

  const close = onClose;

  const closeAction = {
    label: "Close",
    props: { variant: "outline", onClick: close },
  };
  const freeUpSpace = {
    label: "Free up space",
    props: {
      onClick: () => {
        close();
        router.push("/past-usage");
      },
    },
  };
  const footerActions = copy.canFreeUpSpace
    ? // An org admin can fix it here: at critical the fix is the only way out.
      [...(copy.isCritical ? [] : [closeAction]), freeUpSpace]
    : // Nobody else can act on it, so the only action is to acknowledge it.
      [closeAction];

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && close()}
      size="2xl"
      title={
        <span className="flex items-center gap-2">
          <Icon className={`size-5 ${tone}`} />
          {copy.title}
        </span>
      }
      footerActions={
        // Nothing to "close" once the platform is already disrupted: the only
        // action offered is the fix. The header X remains the way out.
        footerActions.length > 0 ? footerActions : undefined
      }
    >
      <div className="flex flex-col gap-4 px-1">
        <NoxusAlert
          variant={copy.isCritical ? "destructive" : "warning"}
          direction="vertical"
          icon={Icon}
          title={copy.usage}
          description={
            <span className="flex flex-col gap-2.5">
              <Progress
                value={copy.percentage}
                className={
                  copy.isCritical ? "bg-destructive/20" : "bg-amber-500/20"
                }
                indicatorClassName={
                  copy.isCritical ? "bg-destructive" : "bg-amber-500"
                }
              />
              <span>{copy.urgency}</span>
              {copy.thresholds && <span>{copy.thresholds}</span>}
            </span>
          }
        />

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-foreground">
            {copy.consequencesHeading}
          </p>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1 list-disc pl-5 text-sm">
            {copy.consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
            <li className="col-span-2">{copy.catchAll}</li>
          </ul>
        </div>

        <Separator />

        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">How to fix it</p>
          <p className="text-sm leading-relaxed">{copy.solution}</p>
        </div>
      </div>
    </BaseDialog>
  );
};
